// frontend/src/components/NameGenerator.js
//
// The generator engine behind the /tools/*-name-generator family.
//
// QUICK NAMES ARE ENTIRELY CLIENT-SIDE, DELIBERATELY. Every competitor on this SERP advertises
// "AI-powered" — Buffer, Hootsuite, Ahrefs, Birdeye. We do not call a model here
// and the reason is economics: `instagram name generator` carries a CPC of $0.08.
// The traffic is worth approximately nothing per click, so a per-generation model
// cost would exceed the value of the visitor. Word banks answer this query just as
// well, run instantly, cost nothing, and carry no rate limit — which is a better
// experience than the 3-per-session cap the AI tools on this site need.
//
// THE CHARACTER LIMIT IS ENFORCED IN CODE, NOT REQUESTED IN A PROMPT. When the
// bio generator asked a model to stay under 80 characters, 3 of 5 TikTok bios came
// back over. Here `fits()` filters the list and nothing over the limit can reach
// the screen.
//
// WE DO NOT CLAIM AVAILABILITY CHECKING. Hopper HQ's snippet promises handles
// "pre-checked for availability"; doing that means hammering Instagram from the
// browser, which their API does not allow and which would break the moment they
// rate-limit us. Instead each result links to the profile URL so the visitor can
// check in one click, and the copy says exactly what a 404 there means.
//
// WHAT WE DO CHECK IS OUR OWN NAMESPACE (Growth round, T21). Each result shows
// whether greatlysocial.com/u/<name> is free as a link in bio — that is our
// database, so the answer is real. Plus a ♡ shortlist kept on this device and,
// after a copy, a "Nice pick" panel with the next three steps. Mockups:
// .jarvis/design/Main.dc.html (desktop) and Phone.dc.html (bottom bar).
//
// THE AI TAB IS OPT-IN (Growth round, T22). On the handle pages a second tab,
// 'AI names from your vibe' (VibeNames.js), asks a model for names with a
// meaning line. Logged out that is one free try a day with a global daily cap,
// so the cost argument above still holds; the word banks stay the default.
//
// THE AI PROFILE KIT (Growth round, T23) opens under the 'Nice pick' panel on
// the same handle pages: a bio, first-post captions and hashtags for the
// copied name (ProfileKit.js, mockup AIKit.dc.html).
//
// SIGN-UP TRACKING (Growth round, T25): the page remembers itself as the
// visitor's first touch and reports the funnel steps (visit, names made,
// claim clicks…) to the admin page's 'Sign-ups by source' (utils/funnel.js).
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Form, Row, Col } from 'react-bootstrap'
import { Clipboard, CheckLg, ArrowRepeat, BoxArrowUpRight, Heart, HeartFill } from 'react-bootstrap-icons'

import {
  BIO_HOST, claimNameFor, isClaimable, checkClaimNames,
  readShortlist, writeShortlist, signupHref,
} from '../utils/nameClaim'
import { saveNameContext } from '../utils/onboarding'
import { captureFirstTouch, trackFunnel } from '../utils/funnel'
import VibeNames, { Sparkle } from './VibeNames'
import ProfileKit from './ProfileKit'

/* ── Word banks ───────────────────────────────────────────────────────────────
   Keyed by the `bank` field in name-generators.json so a new page adds a bank
   rather than a new component. */

const STYLE_WORDS = {
  clean:   ['hq', 'co', 'official', 'media', 'studio', 'works', 'group', 'team', 'daily'],
  playful: ['vibes', 'daily', 'club', 'squad', 'crew', 'zone', 'party', 'gang', 'world'],
  aesthetic: ['bloom', 'haze', 'dusk', 'moon', 'honey', 'velvet', 'cloud', 'petal', 'muse',
              'ember', 'willow', 'sage', 'luna', 'aura', 'dreamy', 'soft', 'lush', 'wildflower'],
  business: ['hq', 'co', 'official', 'group', 'studio', 'agency', 'collective', 'partners'],
}

const PREFIXES = {
  clean:   ['the', 'real'],
  playful: ['hey', 'its', 'just', 'iam'],
  aesthetic: ['soft', 'the', 'lil'],
  business: ['the', 'ask'],
}

// Platform flavour, mixed in alongside the style words.
const BANKS = {
  instagram: ['gram', 'shots', 'feed', 'grid', 'captures', 'diary'],
  tiktok:    ['clips', 'edits', 'pov', 'tok', 'cuts', 'takes'],
  youtube:   ['tv', 'studio', 'channel', 'films', 'media', 'show', 'cast', 'reviews'],
  aesthetic: ['core', 'era', 'mood', 'hour', 'season', 'diary'],
  podcast:   ['pod', 'show', 'talks', 'radio', 'cast', 'sessions', 'hour', 'diaries'],
}

// `name` mode — real titles with spaces, not handles.
const NAME_NOUNS = {
  youtube: ['TV', 'Studio', 'Channel', 'Files', 'Lab', 'Works', 'Media', 'Films', 'Show',
            'Diaries', 'Academy', 'Reviews', 'Breakdown', 'Guide', 'Sessions'],
  podcast: ['Hour', 'Show', 'Talks', 'Sessions', 'Diaries', 'Files', 'Notes', 'Report',
            'Roundtable', 'Breakdown', 'Rundown', 'Club', 'Radio', 'Stories', 'Journal'],
}
const NAME_ADJECTIVES = {
  youtube: ['Everyday', 'Modern', 'Honest', 'Simple', 'Real', 'Curious', 'Daily'],
  podcast: ['Honest', 'Unfiltered', 'Daily', 'Weekly', 'Real', 'Modern', 'Quiet', 'Curious'],
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

/** Strip a seed down to what a handle may legally contain. */
const clean = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '')

/** Title-case a word for `name` mode. */
const title = s => String(s || '').trim().replace(/\s+/g, ' ')
  .replace(/(^|\s)([a-z])/g, (_, a, b) => a + b.toUpperCase())

const shuffle = arr => {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

const pick = (arr, n) => shuffle(arr).slice(0, n)

/** A handle suggestion derived from a display name — shown under `name` mode. */
const toHandle = (name, limit) => clean(name).slice(0, limit)

/**
 * Build the candidate list. Pure, so it is trivially testable and cannot depend
 * on anything React is doing.
 */
export function generate (seed, niche, style, cfg) {
  const full = clean(seed)
  const n = clean(niche)
  if (!full) return []

  // A seed longer than the platform allows would leave room for nothing else and
  // return an almost-empty list. Trim it so combinations still fit — TikTok's
  // limit is 24, and "averyverylongbrandname" alone is 22 of them.
  const room = Math.max(6, cfg.limit - 8)
  const s = full.length > room ? full.slice(0, room) : full

  const limit = cfg.limit
  const seps = cfg.separators || ['', '.', '_']
  const styleWords = STYLE_WORDS[style] || STYLE_WORDS.clean
  const prefixes = PREFIXES[style] || PREFIXES.clean
  const platformWords = BANKS[cfg.bank] || []
  const words = shuffle(styleWords.concat(platformWords))

  const out = []

  if (cfg.mode === 'name') {
    const nouns = NAME_NOUNS[cfg.bank] || NAME_NOUNS.podcast
    const adjs = NAME_ADJECTIVES[cfg.bank] || NAME_ADJECTIVES.podcast
    const S = title(seed)
    const N = niche ? title(niche) : ''

    for (const noun of pick(nouns, 6)) out.push(`The ${S} ${noun}`)
    for (const noun of pick(nouns, 5)) out.push(`${S} ${noun}`)
    for (const adj of pick(adjs, 4)) out.push(`${adj} ${S}`)
    for (const noun of pick(nouns, 3)) out.push(`${noun} with ${S}`)
    if (N) {
      for (const noun of pick(nouns, 4)) out.push(`${S} ${N} ${noun}`)
      out.push(`${S} & ${N}`)
      out.push(`The ${N} ${pick(nouns, 1)[0]}`)
    }
    out.push(`${S} & Co`)
  } else {
    // prefix + seed
    for (const p of prefixes) out.push(p + s)
    // seed + word, across separators
    for (const w of words.slice(0, 10)) {
      for (const sep of seps) out.push(s + sep + w)
    }
    // niche combinations — the highest-quality results when a niche is given
    if (n) {
      for (const sep of seps) out.push(s + sep + n)
      for (const sep of seps) out.push(n + sep + s)
      for (const w of words.slice(0, 4)) out.push(s + n + w)
    }
    // prefix + seed + word
    for (const p of prefixes.slice(0, 2)) {
      for (const w of words.slice(0, 4)) out.push(p + s + w)
    }
    // doubled and dotted — common aesthetic patterns. The dotted form is only
    // offered when it fits WHOLE; slicing it to length used to leave a trailing
    // period, which Instagram and TikTok both reject.
    out.push(s + s)
    const dotted = s.split('').join('.')
    if (s.length > 2 && dotted.length <= limit) out.push(dotted)
  }

  // Enforce the limit HERE. Nothing over it reaches the screen.
  const fits = v => v.length <= limit && v.length >= (cfg.min || 3)
  // A handle may not start or end with a separator, and may not repeat one.
  const tidy = v => cfg.mode === 'name'
    ? v.replace(/\s+/g, ' ').trim()
    : v.replace(/[._]{2,}/g, m => m[0]).replace(/^[._]+|[._]+$/g, '')

  const seen = new Set()
  const unique = []
  for (const raw of out) {
    const v = tidy(raw)
    const key = v.toLowerCase()
    if (!fits(v) || seen.has(key)) continue
    seen.add(key)
    unique.push(v)
  }

  // prefix + seed ('softluna', 'thereal…') are the most natural handles, so
  // they always survive the cut to 24 instead of taking their chances.
  const core = new Set(cfg.mode === 'name' ? [] : prefixes.map(p => tidy(p + s).toLowerCase()))
  const keep = unique.filter(v => core.has(v.toLowerCase()))
  const rest = shuffle(unique.filter(v => !core.has(v.toLowerCase())))
  return shuffle(keep.concat(rest.slice(0, Math.max(0, 24 - keep.length)))).slice(0, 24)
}

/* ── Claim lines, shortlist, next steps (T21) ──────────────────────────────── */

export const GRID_PLANNER_PATH = '/tools/instagram-grid-planner'

/** The bio generator that matches this page's platform. */
export const bioPathFor = cfg =>
  cfg.bank === 'tiktok' ? '/tools/tiktok-bio-generator' : '/tools/instagram-bio-generator'

/** 'Write a matching bio' link: BioGenerator reads ?name= and ?style=. */
export const bioHrefFor = (cfg, value, style) =>
  `${bioPathFor(cfg)}?${new URLSearchParams({ name: value, style })}`

const sameName = (a, b) => a.toLowerCase() === b.toLowerCase()

const LINE = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
  borderRadius: 6, padding: '6px 8px', textDecoration: 'none',
}
const Dot = ({ color }) => (
  <span aria-hidden='true' style={{ width: 8, height: 8, borderRadius: 4, background: color, flex: 'none' }} />
)

/** Under each result: is greatlysocial.com/u/<username> free? */
function ClaimLine ({ username, status, from }) {
  if (!username) return null
  if (status === 'checking') {
    return (
      <div style={{ ...LINE, color: '#6c757d', background: '#f8f9fa' }}>
        <Dot color='#dee2e6' />
        <span className='text-truncate'>Checking {BIO_HOST}{username}…</span>
      </div>
    )
  }
  // The check failed (offline, rate limited): say nothing rather than guess.
  if (!status) return null
  if (status.available) {
    return (
      <a href={signupHref({ claim: username, from })} data-claim='free'
         onClick={() => trackFunnel('claim_click', from)}
         style={{ ...LINE, color: '#146c43', background: '#e8f5ee' }}>
        <Dot color='#198754' />
        <span className='text-truncate' style={{ flex: 1, minWidth: 0 }}>
          <span className='d-none d-sm-inline'>{BIO_HOST}{username} is free</span>
          <span className='d-sm-none'>Free as your link in bio</span>
        </span>
        <strong>Claim</strong>
      </a>
    )
  }
  return (
    <div data-claim='taken' style={{ ...LINE, color: '#495057', background: '#f1f3f5' }}>
      <Dot color='#adb5bd' />
      <span style={{ flex: 1 }}>
        {status.reason === 'Taken as a link in bio' ? status.reason : 'Not available as a link in bio'}
      </span>
    </div>
  )
}

/** Desktop: inline bar in the tool card. Phone: dark bar pinned to the bottom. */
function ShortlistBar ({ names, href, onClear }) {
  if (!names.length) return null
  return (
    <>
      <div className='d-none d-md-flex align-items-center gap-3 mt-3' data-testid='shortlist-bar'
           style={{ border: '1px solid #f5c2c7', background: '#fff8f8', borderRadius: 10, padding: '10px 14px' }}>
        <HeartFill size={18} color='#b02a37' aria-hidden='true' className='flex-shrink-0' />
        <span style={{ flex: 1, fontSize: 15, minWidth: 0 }}>
          <strong>Your shortlist ({names.length}):</strong> {names.join(', ')}{' '}
          <span className='text-muted'>— saved on this device</span>
        </span>
        <Button variant='link' size='sm' className='text-muted p-0 flex-shrink-0' onClick={onClear}>
          Clear
        </Button>
        <a href={href} className='btn btn-primary btn-sm fw-semibold flex-shrink-0'>
          Save it to a free account
        </a>
      </div>
      {/* right: 80px leaves the chat launcher in the corner uncovered. */}
      <div className='d-flex d-md-none align-items-center' data-testid='shortlist-bar-phone'
           style={{
             position: 'fixed', left: 12, right: 80, bottom: 16, zIndex: 1030, gap: 10,
             background: '#212529', color: '#fff', borderRadius: 14, padding: '12px 14px',
             boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
           }}>
        <HeartFill size={18} color='#ff8a95' aria-hidden='true' className='flex-shrink-0' />
        <span className='text-truncate' style={{ flex: 1, fontSize: 14, minWidth: 0 }}>
          <strong>{names.length} saved</strong> · {names.join(', ')}
        </span>
        <a href={href} aria-label='Keep them: save your shortlist to a free account'
           style={{
             height: 40, padding: '0 12px', borderRadius: 8, background: '#fff', color: '#212529',
             fontWeight: 700, fontSize: 14, textDecoration: 'none', display: 'flex', alignItems: 'center', flex: 'none',
           }}>
          Keep them
        </a>
      </div>
    </>
  )
}

const STEP = {
  display: 'flex', flexDirection: 'column', gap: 4, border: '1px solid #e5e7eb',
  borderRadius: 10, padding: 14, textDecoration: 'none', color: '#212529', height: '100%',
}
const STEP_KICKER = { fontSize: 12, fontWeight: 700, color: '#6c757d' }
const STEP_TITLE = { fontWeight: 700, fontSize: 16 }
const STEP_BODY = { fontSize: 14, color: '#495057' }
const STEP_CTA = { fontSize: 14, fontWeight: 600, color: '#0a58ca' }

/** The 'Nice pick' panel shown after a copy. */
function NextSteps ({ cfg, value, style, styleLabel, status, panelRef }) {
  const username = claimNameFor(value)
  const claimable = isClaimable(username)
  const known = status && status !== 'checking'
  const taken = claimable && known && !status.available
  const claimHref = signupHref({ claim: claimable && !taken ? username : '', from: cfg.slug })
  const shown = cfg.mode === 'name' ? value : `@${value}`

  let claimBody, claimCta
  if (!claimable) {
    claimBody = `Get your own ${BIO_HOST} page with a free account.`
    claimCta = 'Claim my link in bio →'
  } else if (taken) {
    claimBody = `${BIO_HOST}${username} is taken, but you can pick another free name when you sign up.`
    claimCta = 'Claim my link in bio →'
  } else if (known) {
    claimBody = `${BIO_HOST}${username} is still free. Grab it before someone else does.`
    claimCta = `Claim ${username} →`
  } else {
    claimBody = `Get ${BIO_HOST}${username} with a free account.`
    claimCta = `Claim ${username} →`
  }

  return (
    <div ref={panelRef} data-testid='next-steps' className='mt-4'
         style={{ background: '#fff', border: '2px solid #0d6efd', borderRadius: 12, padding: '20px 24px' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0a58ca', letterSpacing: 0.4, textTransform: 'uppercase' }}>
        Copied {shown}
      </div>
      <h2 className='fw-bold mt-1 mb-3' style={{ fontSize: 22 }}>
        Nice pick. Now make the rest of your profile match.
      </h2>
      <Row className='g-3'>
        <Col md={4}>
          <a href={bioHrefFor(cfg, value, style)} style={STEP}>
            <span style={STEP_KICKER}>STEP 1 · FREE</span>
            <span style={STEP_TITLE}>Write a matching bio</span>
            <span style={STEP_BODY}>
              Opens the bio generator with “{value}” and the {String(styleLabel).toLowerCase()} style already filled in.
            </span>
            <span style={STEP_CTA}>Write my bio →</span>
          </a>
        </Col>
        <Col md={4}>
          <a href={GRID_PLANNER_PATH} style={STEP}>
            <span style={STEP_KICKER}>STEP 2 · FREE</span>
            <span style={STEP_TITLE}>Plan your first 9 posts</span>
            <span style={STEP_BODY}>Drag your photos into the grid planner and see how the profile will look.</span>
            <span style={STEP_CTA}>Plan my grid →</span>
          </a>
        </Col>
        <Col md={4}>
          <a href={claimHref} onClick={() => trackFunnel('claim_click', cfg.slug)}
             style={{ ...STEP, border: '1px solid #a3cfbb', background: '#f3faf6' }}>
            <span style={{ ...STEP_KICKER, color: '#146c43' }}>STEP 3 · FREE ACCOUNT</span>
            <span style={STEP_TITLE}>Claim your link in bio</span>
            <span style={STEP_BODY}>{claimBody}</span>
            <span style={{ ...STEP_CTA, color: '#146c43' }}>{claimCta}</span>
          </a>
        </Col>
      </Row>
    </div>
  )
}

/* ── Quick names / AI names from your vibe (T22) ───────────────────────────── */

const MODES = [['quick', 'Quick names'], ['ai', 'AI names from your vibe']]
const tabId = (slug, m) => `ng-tab-${m}-${slug}`
const panelId = (slug, m) => `ng-panel-${m}-${slug}`

/** Tabs per the WAI-ARIA pattern: arrow keys move between them. */
function ModeSwitch ({ slug, mode, onChange }) {
  const onKeyDown = e => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
    e.preventDefault()
    const i = MODES.findIndex(([m]) => m === mode)
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? MODES.length - 1
      : (i + (e.key === 'ArrowRight' ? 1 : MODES.length - 1)) % MODES.length
    onChange(MODES[next][0])
    const el = document.getElementById(tabId(slug, MODES[next][0]))
    if (el) el.focus()
  }
  return (
    <div role='tablist' aria-label='Generator mode' className='d-inline-flex flex-wrap mb-3'
         style={{ gap: 6, background: '#f1f3f5', borderRadius: 10, padding: 4 }}>
      {MODES.map(([m, label]) => {
        const on = mode === m
        return (
          <button key={m} type='button' role='tab' id={tabId(slug, m)} aria-selected={on}
                  aria-controls={panelId(slug, m)} tabIndex={on ? 0 : -1}
                  onClick={() => onChange(m)} onKeyDown={onKeyDown}
                  style={{
                    height: 36, padding: '0 16px', border: 0, borderRadius: 8, fontWeight: 600, fontSize: 14,
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: on ? '#fff' : 'transparent',
                    color: on ? (m === 'ai' ? '#4338ca' : '#212529') : '#495057',
                    boxShadow: on ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                  }}>
            {m === 'ai' && <Sparkle size={15} twin />}
            {label}
          </button>
        )
      })}
    </div>
  )
}

/* ── Component ───────────────────────────────────────────────────────────── */

const labelOf = (cfg, s) => ((cfg.styles || []).find(([v]) => v === s) || [s, s])[1]

export default function NameGenerator ({ cfg }) {
  const [seed, setSeed] = useState('')
  const [niche, setNiche] = useState('')
  const [style, setStyle] = useState(cfg.defaultStyle || 'clean')
  const [results, setResults] = useState([])
  const [resultStyle, setResultStyle] = useState(style)    // the style `results` were made in
  const [resultNiche, setResultNiche] = useState('')       // and the niche
  const [copied, setCopied] = useState('')
  const [touched, setTouched] = useState(false)
  const [claims, setClaims] = useState({})                 // username → 'checking' | {available, reason} | null
  const [shortlist, setShortlist] = useState(readShortlist)
  const [picked, setPicked] = useState(null)               // { value, style, about } of the last copy
  const [mode, setMode] = useState('quick')                // 'quick' | 'ai' (handle pages only)
  const [aiClaims, setAiClaims] = useState({})             // username → claim, from the AI names
  const aiAvailable = cfg.mode !== 'name'
  const panelRef = useRef(null)
  const scrolledToPanel = useRef(false)

  const styleLabel = s => labelOf(cfg, s)

  // What AI Studio is prefilled with after a sign-up from this page (T24).
  const remember = (name, fromStyle, about) => saveNameContext({
    name, style: fromStyle, styleLabel: styleLabel(fromStyle), ...about, from: cfg.slug,
  })

  const run = useCallback(e => {
    if (e) e.preventDefault()
    setTouched(true)
    setResultStyle(style)
    setResultNiche(niche.trim())
    const made = generate(seed, niche, style, cfg)
    setResults(made)
    saveNameContext({ style, styleLabel: labelOf(cfg, style), niche: niche.trim(), from: cfg.slug })
    if (made.length) trackFunnel('names_generated', cfg.slug)
  }, [seed, niche, style, cfg])

  // One visit per page load; the first generator page they ever open is
  // their sign-up source.
  const visited = useRef('')
  useEffect(() => {
    if (visited.current === cfg.slug) return
    visited.current = cfg.slug
    captureFirstTouch(cfg.slug)
    trackFunnel('page_view', cfg.slug)
  }, [cfg.slug])

  // One batch check per generated list. A newer list makes older answers stale.
  useEffect(() => {
    const usernames = [...new Set(results.map(claimNameFor).filter(isClaimable))]
    if (!usernames.length) { setClaims({}); return undefined }
    let live = true
    setClaims(Object.fromEntries(usernames.map(u => [u, 'checking'])))
    checkClaimNames(usernames).then(found => {
      if (live) setClaims(Object.fromEntries(usernames.map(u => [u, found[u] || null])))
    })
    return () => { live = false }
  }, [results])

  // Bring the panel into view the first time it appears, not on every copy.
  useEffect(() => {
    if (!picked || scrolledToPanel.current || !panelRef.current) return
    scrolledToPanel.current = true
    if (typeof panelRef.current.scrollIntoView === 'function') {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [picked])

  // `about` (the niche or the AI vibe) gives the profile kit something to go on.
  const copy = async (value, fromStyle = resultStyle, about = resultNiche, aboutIsVibe = false) => {
    // The panel opens even when the clipboard is blocked: they chose the name.
    setPicked({ value, style: fromStyle, about })
    remember(value, fromStyle, aboutIsVibe ? { vibe: about } : { niche: about })
    try {
      await navigator.clipboard.writeText(value)
      setCopied(value)
      setTimeout(() => setCopied(''), 1400)
    } catch { /* clipboard blocked — the text is selectable anyway */ }
  }

  const isSaved = value => shortlist.some(n => sameName(n, value))
  const toggleSaved = value => {
    setShortlist(writeShortlist(isSaved(value)
      ? shortlist.filter(n => !sameName(n, value))
      : [...shortlist, value]))
  }

  return (
    <>
    <div className='p-4 rounded-3' style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
      {aiAvailable && <ModeSwitch slug={cfg.slug} mode={mode} onChange={setMode} />}

      {aiAvailable && (
        <div role='tabpanel' id={panelId(cfg.slug, 'ai')} aria-labelledby={tabId(cfg.slug, 'ai')}
             hidden={mode !== 'ai'}>
          <VibeNames cfg={cfg} copied={copied} onCopy={(value, vibe) => copy(value, style, vibe, true)}
                     isSaved={isSaved} onToggleSaved={toggleSaved} onClaims={setAiClaims} />
        </div>
      )}

      <div {...(aiAvailable
        ? { role: 'tabpanel', id: panelId(cfg.slug, 'quick'), 'aria-labelledby': tabId(cfg.slug, 'quick'), hidden: mode !== 'quick' }
        : {})}>
      <Form onSubmit={run}>
        <Row className='g-3 align-items-end'>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>{cfg.seedLabel}</Form.Label>
            <Form.Control
              value={seed}
              onChange={e => setSeed(e.target.value)}
              placeholder={cfg.seedPlaceholder}
              maxLength={30}
            />
          </Col>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>
              {cfg.nicheLabel} <span className='text-muted fw-normal'>(optional)</span>
            </Form.Label>
            <Form.Control
              value={niche}
              onChange={e => setNiche(e.target.value)}
              placeholder={cfg.nichePlaceholder}
              maxLength={30}
            />
          </Col>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>Style</Form.Label>
            <Form.Select value={style} onChange={e => setStyle(e.target.value)}>
              {(cfg.styles || []).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        <div className='mt-3 d-flex gap-2 align-items-center flex-wrap'>
          <Button type='submit' variant='primary'>
            {results.length ? <><ArrowRepeat className='me-1' />Generate again</> : `Generate ${cfg.noun}`}
          </Button>
          <span className='text-muted small'>
            {results.length
              ? 'Tap ♡ to save names you like. Green means the name is free as your link in bio.'
              : `${cfg.limit}-character limit, enforced — nothing longer is shown.`}
          </span>
        </div>
      </Form>

      {touched && !clean(seed) && (
        <p className='text-danger small mt-3 mb-0'>Enter a word or name to start.</p>
      )}

      {results.length > 0 && (
        <>
          <hr className='my-4' />
          <Row className='g-2'>
            {results.map(value => {
              const handle = cfg.mode === 'name' ? toHandle(value, cfg.limit) : value
              const username = claimNameFor(value)
              const saved = isSaved(value)
              return (
                <Col md={6} lg={4} key={value}>
                  <div className='d-flex flex-column gap-2 p-2 rounded-3 h-100' data-testid='name-result'
                       style={{ border: '1px solid #e5e7eb' }}>
                    <div className='d-flex align-items-center justify-content-between gap-2'>
                      <div style={{ minWidth: 0 }}>
                        <div className='fw-semibold text-truncate'>
                          {cfg.mode === 'name' ? value : `@${value}`}
                        </div>
                        <div className='text-muted small text-truncate'>
                          {cfg.mode === 'name' && <>@{handle} · </>}
                          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {value.length}/{cfg.limit}
                          </span>
                        </div>
                      </div>
                      <div className='d-flex gap-1 flex-shrink-0'>
                        <Button size='sm' variant='light' onClick={() => toggleSaved(value)}
                                aria-pressed={saved}
                                title={saved ? 'Remove from shortlist' : 'Save to shortlist'}
                                aria-label={saved ? `Remove ${value} from shortlist` : `Save ${value}`}
                                style={saved
                                  ? { border: '1px solid #f5c2c7', background: '#fff0f1', color: '#b02a37' }
                                  : { border: '1px solid #ced4da', background: '#fff', color: '#6c757d' }}>
                          {saved ? <HeartFill /> : <Heart />}
                        </Button>
                        <Button size='sm' variant='outline-secondary' onClick={() => copy(value)}
                                title='Copy' aria-label={`Copy ${value}`}>
                          {copied === value ? <CheckLg /> : <Clipboard />}
                        </Button>
                        {cfg.checkUrl && (
                          <Button size='sm' variant='outline-secondary' href={cfg.checkUrl + handle}
                                  target='_blank' rel='noopener noreferrer'
                                  title={`Check ${handle} on ${cfg.platformLabel}`}
                                  aria-label={`Check ${handle} on ${cfg.platformLabel}`}>
                            <BoxArrowUpRight />
                          </Button>
                        )}
                      </div>
                    </div>
                    {isClaimable(username) && (
                      <ClaimLine username={username} status={claims[username]} from={cfg.slug} />
                    )}
                  </div>
                </Col>
              )
            })}
          </Row>
          {cfg.checkUrl && (
            <p className='text-muted small mt-3 mb-0'>
              The arrow opens that profile on {cfg.platformLabel}. If it says the page is not
              available, the handle is free. We do not check availability for you — anyone who
              claims to is guessing, because {cfg.platformLabel} does not publish that data.
              The green line is different: greatlysocial.com links are ours, so that check is real.
            </p>
          )}
        </>
      )}
      </div>

      <ShortlistBar names={shortlist} href={signupHref({ from: cfg.slug })}
                    onClear={() => setShortlist(writeShortlist([]))} />
    </div>

    {picked && (
      <NextSteps cfg={cfg} value={picked.value} style={picked.style} styleLabel={styleLabel(picked.style)}
                 status={claims[claimNameFor(picked.value)] || aiClaims[claimNameFor(picked.value)]}
                 panelRef={panelRef} />
    )}

    {picked && aiAvailable && (
      <ProfileKit key={picked.value} cfg={cfg} name={picked.value} style={picked.style}
                  styleLabel={styleLabel(picked.style)} vibe={picked.about}
                  status={claims[claimNameFor(picked.value)] || aiClaims[claimNameFor(picked.value)]} />
    )}
    </>
  )
}

